<template>
  <!-- Flag ON: the admin-configured header. Flag OFF: the legacy markup
       below, byte-identical to what shipped before this module. A v-if/v-else
       pair at template root avoids wrapping the legacy tree in an extra
       element, so the flag-off DOM is unchanged. -->
  <dynamic-top-menu v-if="megaMenuEnabled" />

  <div
    v-else
    class="top-menu"
    :class="{ 'top-menu--overflowing': topMenuOverflowing }"
  >
    <ul ref="topMenuNav" class="nav">
      <li
        v-for="item in menuItems"
        :key="item.linkName"
        class="nav-item"
        :class="{ 'item-active': isMenuItemActive(item) }"
        @mouseenter="openPopover(item.name)"
        @mouseleave="closePopover"
      >
        <router-link
          v-if="item.linkName !== 'products' && item.linkName !== 'shows&events'"
          :to="{ name: item.linkName }"
          class="nav-link"
          :class="{ 'active': routerName === item.linkName }"
        >
          {{ item.name }}
        </router-link>
        <a
          v-else-if="item.linkName === 'shows&events'"
          class="nav-link"
          :class="{ 'item-active': routerName === item.linkName }"
          style="cursor:pointer;"
        >
          {{ item.name }}
        </a>
        <a
          v-else
          class="nav-link"
          :class="{ 'item-active': routerName === item.linkName }"
        >
          <popper
            ref="productTopMenuPopperRef"
            trigger="hover"
            :options="{
              placement: 'top',
              modifiers: {
                offset: { offset: '0,5px' },
                preventOverflow: { enabled: true },
                hide: { enabled: true },
              },
              strategy: 'fixed',
            }"
            :visible-arrow="false"
          >
            <div
              class="popper"
              style="width: 100%;"
            >
              <div class="popover-container">
                <product-dropdown-menu v-if="isPopoverOpen && selectedItem === 'Products'" />
              </div>
            </div>
            <div
              slot="reference"
              style="cursor:pointer;"
            >
              <span>{{ item.name }}</span>
            </div>
          </popper>
        </a>
      </li>
    </ul>
    <button
      type="button"
      class="top-menu-scroll-arrow"
      aria-label="Scroll menu"
      @click="scrollTopMenu"
    >
      <span aria-hidden="true">&#8250;</span>
    </button>
  </div>
</template>

<script>
import { TOP_MENU_ITEMS } from '~/constants'
import ProductDropdownMenu from '~/components/common/TopMenu/ProductDropdownMenu/ProductDropdownMenu.vue'
import DynamicTopMenu from '~/components/common/TopMenu/DynamicTopMenu.vue'
import { EventBus } from '~/helpers/miscs'
import topMenuScroll from '~/mixins/topMenuScroll'
import { mapActions, mapGetters } from 'vuex'

export default {
  components: {
    ProductDropdownMenu,
    DynamicTopMenu
  },
  mixins: [topMenuScroll],
  data: () => ({
    menuItems: [...TOP_MENU_ITEMS],
    isPopoverOpen: false,
    selectedItem: null
  }),
  computed: {
    ...mapGetters({
      authUser: 'auth/user',
      megaMenuEnabled: 'settings/megaMenuEnabled'
    }),
    routerName () {
      return this.$route.name
    },
    isAdminOrRep() {
      return this.authUser && (this.authUser.role === 'admin' || this.authUser.role === 'rep')
    },
    userName() {
      if (this.authUser) {
        return `${this.authUser.first_name} ${this.authUser.last_name}`.trim() || this.authUser.name
      }
      return ''
    }
  },
  methods: {
    ...mapActions({
      fetchCommonMenu: 'menus/fetchCommonMenu'
    }),
    openPopover(itemName) {
      this.isPopoverOpen = true
      this.selectedItem = itemName
      if (itemName === 'Products') {
        this.fetchCommonMenu()
      }
    },
    closePopover() {
      // this.isPopoverOpen = false;
      // this.selectedItem = null;
    },
    isMenuItemActive(item) {
      return this.routerName === item.linkName
    },
    customBoundaryFunction(popperOffsets, data) {
      return {
        left: popperOffsets.left, // Allow overflow to the left
        right: popperOffsets.right, // Allow overflow to the right
        top: data.rects.reference.top, // Keep the top boundary as is
        bottom: data.rects.reference.bottom // Keep the bottom boundary as is
      }
    }
  },
  mounted () {
    if (this.isAdminOrRep && this.userName) {
      const shopByGalleryIndex = this.menuItems.findIndex(item => item.linkName === 'shop-by-gallery')
      if (shopByGalleryIndex !== -1 && !this.menuItems.some(i => i.name === this.userName)) {
        this.menuItems.splice(shopByGalleryIndex + 1, 0, {
          name: this.userName,
          linkName: 'settings'
        })
      }
    }

    EventBus.$on('close-product-popper', () => {
      this.$refs?.productTopMenuPopperRef?.[0]?.doClose()
    })
  }
}
</script>

<style scoped lang="scss">
.top-menu {
  background-color: #ffffff;
  border-bottom: 1px solid #e3e2e2;

  > .nav {
    > .nav-item {
      cursor: pointer;
      transition: 0.1s linear all;
      &.item-active,
      &:hover {
        box-shadow: inset 0 -3px 0 0 #383838;
      }
    }
  }

  & > .nav > .nav-item > .nav-link {
      color: #898989;
      padding: 15px 0px;
      margin: 0px 22px;
      text-transform: uppercase;
      font: normal normal normal 15px/20px PlayfairDisplay-Medium;

      @media (min-width: 768px) and (max-width: 1279px) {
        margin: 0px 12px;
      }
      @media (min-width: 744px) and (max-width: 767px) {
        margin: 0px 7px;
      }

      span {
        padding: 18px 0 19px 0;
      }

      &.active,
      &:active {
          box-shadow: inset 0 -3px 0 0 #383838;
          font-weight: bold;
          color: #000000;
      }
  }
}
.popper {
  margin-top: 10px;
  .popover-container {
    height: 100%;
  }
}
@media screen and (max-width:1280px) {
  .popover-container {
    padding-left: 41px;
    padding-right: 41px;
  }
}
@media screen and (max-width:767px) {
  .popover-container {
    padding-left: 17px;
    padding-right: 17px;
  }
}
@media screen and (max-width:744px) {
  .popover-container {
    padding-left: 15px;
    padding-right: 15px;
  }
}
</style>
<style>
.popper-content {
  transform: translate3d(0, 0, 100px); /* Apply 3D translation along the Z-axis */
}

.top-menu-scroll-arrow {
  display: none;
}

@media (min-width: 1280px) {
  .top-menu > .nav {
    justify-content: center;
  }
}

@media (max-width: 1279px) {
  .top-menu {
    position: relative;
  }

  .top-menu > .nav {
    flex-wrap: nowrap !important;
    overflow-x: auto;
    overflow-y: hidden;
    justify-content: flex-start !important;
    padding-left: 12px;
    padding-right: 52px;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .top-menu > .nav::-webkit-scrollbar {
    display: none;
  }

  .top-menu > .nav > .nav-item {
    flex-shrink: 0;
  }

  .top-menu > .nav > .nav-item > .nav-link {
    white-space: nowrap;
  }
}

.top-menu--overflowing .top-menu-scroll-arrow {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px 2px 14px;
  border: 0;
  border-left: 2px solid #b8b8b8;
  color: #898989;
  font-size: 26px;
  font-weight: 300;
  line-height: 1;
  background: #ffffff;
  cursor: pointer;
  z-index: 2;
  transition: color 0.15s ease;
}

.top-menu--overflowing .top-menu-scroll-arrow::before {
  content: '';
  position: absolute;
  right: 100%;
  top: 0;
  bottom: 0;
  width: 32px;
  background: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff 90%);
  pointer-events: none;
}

.top-menu--overflowing .top-menu-scroll-arrow:hover,
.top-menu--overflowing .top-menu-scroll-arrow:focus-visible {
  color: #000;
}

.top-menu--overflowing .top-menu-scroll-arrow:focus-visible {
  outline: 2px solid #000;
  outline-offset: -2px;
}

@media (max-width: 743px) {
  .top-menu > .nav > .nav-item > .nav-link {
    padding: 12px 0;
    margin: 0 14px;
    font-size: 14px;
    line-height: 18px;
  }

  .top-menu > .nav > .nav-item:first-child > .nav-link {
    margin-left: 4px;
  }
}
</style>
